import React from "react";
import { trackAmplitudeEvent } from "../../amplitude";
import Icon from "../Icon";
import Modal from "../Modal";
import { webPlayMode } from "./todaysPlanService";

const SKILLS = {
  listening: { icon: "headphones", label: "Listening" },
  speaking: { icon: "microphone", label: "Speaking" },
  vocabulary: { icon: "book", label: "Vocabulary" }
};

const MODE_COLORS = { flashcard: "#a9d7f5", full_text_input: "#ff7866", multiple_choice: "#ffe578", text_input: "#fcaca2" };

const modeLabel = (mode, skill) => {
  if(mode === "full_text_input") return skill === "listening" ? "Transcribe" : "Translate";
  if(mode === "flashcard") return "Flashcards";
  return mode === "text_input" ? "Text Input" : "Multiple Choice";
};

const percentOf = (count, total) => total > 0 ? Math.min(100, Math.round((count / total) * 100)) : 0;

const Pill = ({ children }) => (
  <span className="todays-plan-pill" style={{ alignItems: "center", background: "#f7f7f7", border: "1px solid #ddd", borderRadius: 6, display: "inline-flex", fontSize: 12, fontWeight: 600, gap: 5, padding: "4px 8px" }}>
    {children}
  </span>
);

export function TodaysPlanInProgressRow({ collectionName, isTranslateReviewsEnabled, nextItem, queue, roundSentenceCount }) {
  const completed = queue.reduce((s, item) => s + Math.min(item.completed, item.total), 0);
  const total = queue.reduce((s, item) => s + item.total, 0);
  const percent = percentOf(completed, total);
  const deltaPercent = Math.max(0, percent - percentOf(Math.max(0, completed - roundSentenceCount), total));
  const mode = webPlayMode(nextItem, isTranslateReviewsEnabled);
  const skill = SKILLS[nextItem.skill] || SKILLS.vocabulary;

  return (
    <div className="panel panel-default" data-testid="todays-plan-in-progress-row">
      <div className="panel-body tw-flex tw-flex-col tw-gap-3">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Icon name="clipboard" style={{ color: "#f4b73b", fontSize: 20 }} type="fa" />
          <span className="joystix" style={{ fontSize: 18 }}>Today's Plan</span>
        </div>
        <div className="progress" style={{ marginBottom: 0 }}>
          <div className="progress-bar progress-bar-success" data-testid="todays-plan-progress-before-round" style={{ width: `${percent - deltaPercent}%` }} />
          <div className="progress-bar progress-bar-success" data-testid="todays-plan-progress-this-round" style={{ opacity: 0.4, width: `${deltaPercent}%` }} />
        </div>
        <small className="text-secondary" data-testid="todays-plan-in-progress-progress">{completed} of {total} sentences done</small>
        <small className="text-secondary" style={{ fontWeight: 700, letterSpacing: 0.5 }}>NEXT UP</small>
        <div className="tw-flex tw-flex-col tw-gap-3" data-testid="todays-plan-next-up" style={{ border: "1px solid #ddd", borderRadius: 12, padding: "12px 14px" }}>
          <strong style={{ fontSize: 16 }}>{collectionName}</strong>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <Pill><Icon name={skill.icon} style={{ color: "#777", fontSize: 11 }} type="fa" />{skill.label}</Pill>
            <Pill><Icon name="circle" style={{ color: MODE_COLORS[mode], fontSize: 8 }} type="fa" />{modeLabel(mode, nextItem.skill)}</Pill>
            <Pill><Icon name="hashtag" style={{ color: "#777", fontSize: 11 }} type="fa" />{nextItem.roundLength} sentences</Pill>
            <Pill>
              <Icon name={nextItem.type === "review" ? "refresh" : "lightbulb-o"} style={{ color: "#777", fontSize: 11 }} type="fa" />{nextItem.type === "review" ? "Reviews" : "New words"}
            </Pill>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TodaysPlanCompleteRow({ freeCardsRemaining }) {
  return (
    <div className="panel panel-default" data-testid="todays-plan-complete-row">
      <div className="panel-body tw-flex tw-items-center tw-gap-4">
        <Icon name="trophy" style={{ color: "#f4b73b", fontSize: 36 }} type="fa" />
        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="joystix" style={{ fontSize: 18 }}>Today's Plan Complete!</span>
          <span className="text-secondary" data-testid="plan-complete-subtitle">
            {freeCardsRemaining > 0
              ? `You still have ${freeCardsRemaining} free sentences left today. Keep playing from your dashboard!`
              : "Consistency is key! Take a well-earned break and come back tomorrow!"}
          </span>
        </div>
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ["#5cb85c", "#f4b73b", "#ff7866", "#a9d7f5", "#ffe578"];
const CONFETTI_COUNT = 140;

export function Confetti() {
  const pieces = React.useMemo(() => Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    delay: Math.random() * 1.5,
    duration: 2.5 + Math.random() * 2,
    left: Math.random() * 100
  })), []);

  return (
    <div aria-hidden="true" data-testid="confetti" style={{ inset: 0, overflow: "hidden", pointerEvents: "none", position: "fixed", zIndex: 1060 }}>
      {pieces.map((piece, i) => (
        <span
          key={i}
          style={{
            animation: `todays-plan-confetti-fall ${piece.duration}s linear ${piece.delay}s forwards`,
            backgroundColor: piece.color,
            height: 12,
            left: `${piece.left}%`,
            position: "absolute",
            top: 0,
            transform: "translateY(-10vh)",
            width: 8
          }}
        />
      ))}
    </div>
  );
}

export function TodaysPlanCelebrationModal({ completedItems, daysPlayed, onHidden, streakDays }) {
  const modalRef = React.useRef(null);
  const shownAtRef = React.useRef(Date.now());
  const hasTrackedDismissRef = React.useRef(false);

  const trackDismissed = () => {
    if(hasTrackedDismissRef.current) return;
    hasTrackedDismissRef.current = true;
    trackAmplitudeEvent("todays_plan_celebration_dismissed", { time_on_screen: Math.round((Date.now() - shownAtRef.current) / 1000) });
  };

  const hide = () => {
    trackDismissed();
    onHidden();
  };

  return (
    <Modal onHidden={hide} ref={modalRef} title="Today's Plan">
      <div className="text-center" data-testid="todays-plan-celebration">
        <Icon name="trophy" style={{ color: "#f4b73b", fontSize: 96 }} type="fa" />
        <div className="joystix todays-plan-gold-text" style={{ fontSize: 20, margin: "8px 0" }}>Today's Plan Complete!</div>
        <p>{completedItems} sentences played today. You're getting more fluent every day. Wahoo!</p>
        {streakDays > 0 && (
          <p className="joystix todays-plan-gold-text">
            <Icon name="fire" type="fa" /> <span data-testid="todays-plan-celebration-streak">{streakDays} day streak</span>
          </p>
        )}
        {daysPlayed > 0 && (
          <p className="joystix todays-plan-gold-text">
            <Icon name="ok" /> <span data-testid="todays-plan-celebration-days-played">{daysPlayed} day{daysPlayed === 1 ? "" : "s"} played</span>
          </p>
        )}
        <button
          className="btn btn-success btn-block btn-lg joystix"
          data-testid="todays-plan-celebration-awesome"
          onClick={() => { trackDismissed(); modalRef.current.hide(); }}
        >
          Awesome!
        </button>
      </div>
    </Modal>
  );
}
